import { defineStore } from "pinia"
import { HttpService } from "@/helpers/http-service"
import { CONSTANT } from "@/helpers/constant"
import { DEFAULT_JC_ROLES, isJcRole } from "@/helpers/jcRoles"
import type { JcRole, JcUsageRoleOption } from "@/types/jcFilterSystem"

const httpService = new HttpService()

interface JcUsageRolesState {
  roles: JcUsageRoleOption[]
  loaded: boolean
}

// Accepts both response shapes the endpoint may use:
//   ["player", "goalkeeper", "coach"]
//   { data: ["player", ...] } / { data: { data: [...] } }
//   [{ role: "player", label: "..." }]
function normalizeResponse(raw: any): JcUsageRoleOption[] {
  const list = Array.isArray(raw) ? raw : raw?.data?.data ?? raw?.data ?? raw?.roles ?? []
  if (!Array.isArray(list)) return []
  const out: JcUsageRoleOption[] = []
  for (const entry of list) {
    const role = typeof entry === "string" ? entry : entry?.role
    if (isJcRole(role)) out.push({ role, label: typeof entry === "object" ? entry?.label ?? null : null })
  }
  return out
}

export const useJcUsageRolesStore = defineStore({
  id: "jc.usageRoles",
  state: (): JcUsageRolesState => ({
    roles: [],
    loaded: false,
  }),
  getters: {
    // Bare role values for iteration; always non-empty thanks to the fallback.
    roleValues(): JcRole[] {
      const values = this.roles.map((r) => r.role)
      return values.length > 0 ? values : DEFAULT_JC_ROLES
    },
  },
  actions: {
    async fetchRoles(force = false): Promise<JcUsageRoleOption[]> {
      if (this.loaded && !force) return this.roles
      try {
        const result = await httpService.get(CONSTANT.API.JERSEY.USAGE_ROLES)
        const normalized = normalizeResponse(result)
        // Fall back to the hardcoded set if the endpoint isn't available yet
        // or returns nothing usable, so the admin UI keeps working.
        this.roles = normalized.length > 0 ? normalized : DEFAULT_JC_ROLES.map((role) => ({ role, label: null }))
      } catch (error) {
        console.warn("usage-roles endpoint unavailable, falling back to defaults", error)
        this.roles = DEFAULT_JC_ROLES.map((role) => ({ role, label: null }))
      } finally {
        this.loaded = true
      }
      return this.roles
    },
  },
})
